<template>
  <div class="nav-page-container">
    <template v-if="isLoading">
      <div class="d-flex align-items-center mt-1 pb-1">
        <placeholder-rect height="24px" width="24px" class="mr-0 my-0" />
        <placeholder-rect height="16px" width="88px" class="my-0" />
      </div>
      <div v-if="!subpage" class="d-flex align-items-center mt-n1">
        <placeholder-rect height="24px" width="24px" class="mr-0" />
        <placeholder-rect height="16px" width="88px" />
      </div>
      <div v-if="!subpage" class="d-flex align-items-center">
        <placeholder-rect height="24px" width="24px" class="mr-0 my-0" />
        <placeholder-rect height="16px" width="88px" class="my-0" />
      </div>
    </template>
    <template v-else>
      <template v-for="page in pageTree">
        <template v-if="!pageAccess[page.id]">
          {{ /* Show loader while access is fetched */ }}
          <div :key="page.id" class="d-flex align-items-center mt-1 pb-1">
            <placeholder-rect height="24px" width="24px" class="mr-0 my-0" />
            <placeholder-rect height="16px" width="88px" class="my-0" />
          </div>
        </template>
        <template v-else>
          <navigation-page-node
            :key="page.id"
            :page="page"
            :subpage="subpage"
          />
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"

import { isPresent } from "@/lib/utils"
import { ROOT_TREE_ID, traverseTree } from "@/lib/page-utils"

import NavigationPageNode from "@/components/NavigationPageNode"
import PlaceholderRect from "@/components/PlaceholderRect"

export default {
  components: {
    NavigationPageNode,
    PlaceholderRect
  },

  props: {
    rootId: { type: [String, Number], default: null },
    subpage: { type: Boolean, default: false }
  },

  data() {
    return {
      rootPage: this.rootId,
      isLoading: false
    }
  },

  computed: {
    ...mapGetters("pages", ["getPageTree", "getPageAccess"]),
    ...mapState("pages", ["currentPage", "pageAccess"]),
    ...mapGetters("auth", ["isAuthenticated"]),

    pageTree() {
      return this.filterOutPrivatePages(this.getPageTree(this.rootPage))
    }
  },

  mounted() {
    if (!this.pageTree) {
      this.fetchTree()
    }
  },

  methods: {
    ...mapActions("pages", ["fetchPageTree", "addToAccessQueue"]),

    filterOutPrivatePages(tree) {
      if (!tree) {
        return tree
      }

      return tree
        .filter(page => {
          const access = this.getPageAccess(page.id)
          return access ? (access.private ? access.granted : true) : true
        })
        .map(page => {
          if (page.pages) {
            page.pages = this.filterOutPrivatePages(page.pages)
          }

          return page
        })
    },

    fetchTree() {
      this.isLoading = true
      this.fetchPageTree(this.rootPage)
        .then(this.fetchTreeAccess)
        .finally(() => (this.isLoading = false))
    },

    fetchTreeAccess() {
      traverseTree(this.getPageTree(this.rootPage), page => {
        this.addToAccessQueue(page.id)
      })
    }
  },

  watch: {
    currentPage: {
      immediate: true,
      handler: function() {
        if (isPresent(this.rootId)) return

        const currentRoot = this.rootPage
        this.rootPage = this.currentPage?.contentRootId || ROOT_TREE_ID

        if (currentRoot !== this.rootPage) {
          this.fetchTree()
        }
      }
    },

    isAuthenticated: {
      immediate: true,
      handler: function() {
        if (this.isAuthenticated) {
          // This can sometimes conflict with an existing request on initial load,
          // therefore we delay it by half a second to ensure the request made after login
          // is preferred
          setTimeout(this.fetchTreeAccess, 500)
        }
      }
    }
  }
}
</script>
